// src/lib/queryClient.ts
import { QueryClient } from '@tanstack/react-query';

// Cache time constants (in milliseconds)
export const CACHE_TIMES = {
  // Session-long cache for user metadata
  SESSION: 1000 * 60 * 60 * 24, // 24 hours

  // Long cache for static/slow-changing data
  LONG: 1000 * 60 * 30, // 30 minutes

  // Medium cache for concept lists, test questions
  MEDIUM: 1000 * 60 * 15, // 15 minutes

  // Short cache for analytics (can be slightly stale)
  ANALYTICS: 1000 * 60 * 10, // 10 minutes

  // Very short for frequently changing data
  SHORT: 1000 * 60 * 5, // 5 minutes
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: CACHE_TIMES.SHORT,
      gcTime: CACHE_TIMES.LONG,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

// Query key factories
export const queryKeys = {
  // Concepts (Plus1)
  concepts: {
    all: ['concepts-plus1'] as const,
    list: (subjectId?: number) =>
      ['concepts-plus1', 'list', subjectId] as const,
    detail: (conceptId: number) =>
      ['concepts-plus1', 'detail', conceptId] as const,
  },

  // Subjects
  subjects: {
    all: ['subjects-plus1'] as const,
  },

  // Flashcards
  flashcards: {
    byConcept: (conceptId: number) =>
      ['flashcards-plus1', 'concept', conceptId] as const,
    bySubject: (subjectId: number) =>
      ['flashcards-plus1', 'subject', subjectId] as const,
  },

  // Tests
  tests: {
    byConcept: (conceptId: number, difficulty?: string) =>
      ['tests-plus1', 'concept', conceptId, difficulty] as const,
    history: (studentId: string) =>
      ['tests-plus1', 'history', studentId] as const,
  },

  // Analytics
  analytics: {
    student: (studentId: string) =>
      ['analytics-plus1', 'student', studentId] as const,
    weakConcepts: (studentId: string) =>
      ['analytics-plus1', 'weakConcepts', studentId] as const,
    strongConcepts: (studentId: string) =>
      ['analytics-plus1', 'strongConcepts', studentId] as const,
  },

  // Badges
  badges: {
    student: (studentId: string) =>
      ['badges-plus1', 'student', studentId] as const,
  },

  // Exam Prep
  examPrep: {
    subjects: ['exam-prep', 'subjects'] as const,
    chapters: (subject: string) =>
      ['exam-prep', 'chapters', subject] as const,
    modules: (subject: string, chapter: string) =>
      ['exam-prep', 'modules', subject, chapter] as const,
    moduleDetail: (moduleId: number) =>
      ['exam-prep', 'module', moduleId] as const,
    content: (moduleId: number) =>
      ['exam-prep', 'content', moduleId] as const,
    solvedExamples: (moduleId: number) =>
      ['exam-prep', 'examples', moduleId] as const,
  },

  // PYQ
  pyq: {
    byConcept: (conceptId: number) =>
      ['pyq', 'concept', conceptId] as const,
    byExam: (examName: string) =>
      ['pyq', 'exam', examName] as const,
  },
};
